// @flow

import { useEffect } from 'react';
import { useTenant } from '@tectonics/commons/tenant';
import { usePayment } from '../context';
import {
  queryHostedPayment,
  QUERY_ERROR_MESSAGE,
  PAYMENT_NOT_PENDING_CODE,
} from '../services/payment-api';
import { PAYMENT_REJECTED_MESSAGE } from './use-approval-polling';
import { resolveWaitingDeadline } from './waiting-deadline';

export function usePaymentQuery() {
  const tenant = useTenant();
  const { data, dispatch } = usePayment();

  useEffect(() => {
    if (data.state !== 'LOADING') return;

    let cancelled = false;

    async function fetchPayment() {
      try {
        const response = await queryHostedPayment(data.paymentHash, tenant.code);

        if (cancelled) return;

        if (response.status === 'ERROR') {
          if (response.status_code === PAYMENT_NOT_PENDING_CODE) {
            dispatch({
              type: 'START_AWAITING_PAYMENT_STATUS',
              waitingDeadline: resolveWaitingDeadline(response.expires_in_seconds),
            });
            return;
          }

          dispatch({
            type: 'SET_ERROR',
            errorMessage: response.status_message || QUERY_ERROR_MESSAGE,
            retryState: 'LOADING',
          });
          return;
        }

        if (response.payment_status === 'CO') {
          dispatch({
            type: 'PAYMENT_QUERY_SUCCESS',
            redirectUrlAfterPayment: response.redirect_url_after_payment ?? null,
          });
          dispatch({ type: 'PAYMENT_CONFIRMED' });
          return;
        }

        if (response.payment_status === 'CA') {
          dispatch({
            type: 'SET_ERROR',
            errorMessage: PAYMENT_REJECTED_MESSAGE,
            retryState: 'LOADING',
          });
          return;
        }

        dispatch({
          type: 'PAYMENT_QUERY_SUCCESS',
          redirectUrlAfterPayment: response.redirect_url_after_payment ?? null,
        });

        if (response.status === 'SUCCESS') {
          dispatch({
            type: 'START_WAITING_APPROVAL',
            waitingDeadline: resolveWaitingDeadline(response.expires_in_seconds),
          });
        }
      } catch (err) {
        if (!cancelled) {
          dispatch({
            type: 'SET_ERROR',
            errorMessage: err.message || QUERY_ERROR_MESSAGE,
            retryState: 'LOADING',
          });
        }
      }
    }

    fetchPayment();

    return () => {
      cancelled = true;
    };
  }, [data.state, data.paymentHash, tenant.code, dispatch]);
}
